import React, { useState, useEffect, FormEvent } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from "../ui/card";
import { Spinner } from "../ui/spinner";
import { MoonIcon, SunIcon, ChevronDownIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon } from "../ui/icons";
import { ErrorPage } from "./ErrorPage";
import { validateReference, getTraditionForTranslation, getLanguageForTranslation } from '../utils/bible-limits';
import { normalizeBookToAcronym, getApiBookCode } from '../utils/bible-standardizer';
import { formatDisplayReference, formatUIReference, getBookDisplayName } from '../utils/bible-displays';
import { convertHebrewPsalmToAelf, getPsalmDisplayName } from '../utils/psalm_reference_converter';
import italianTranslations from '../assets/bible/it/translations.json';
import spanishTranslations from '../assets/bible/es/translations.json';
import germanTranslations from '../assets/bible/de/translations.json';
import bookLimitsData from '../assets/bible/book_limits.json';
import { useCommentaryEntries, CommentaryEntry } from '../hooks/useCommentaryEntries';

const TRANSLATION_NAMES = {
  aelf: 'AELF (Association Épiscopale Liturgique pour les pays Francophones)',
  lsg: 'Louis Segond (1910)',
  mar: 'Bible Martin',
  dar: 'Bible Darby',
  bdp: 'La Bible des Peuples (BDP)',
  drb: 'Douay-Rheims Bible',
  kjv: 'King James Version',
  niv: 'New International Version',
  esv: 'English Standard Version',
  cei08: 'Conferenza Episcopale Italiana (2008)',
  bl95: 'La Biblia de las Américas (BL95)',
  rv60: 'Reina-Valera 1960 (RV60)',
  nvi22: 'Nueva Versión Internacional 2022 (NVI22)',
  eu: 'Einheitsübersetzung 2016 (EU)',
  elb: 'Elberfelder Bibel (ELB)',
  lut: 'Lutherbibel 2017 (LUT)',
  sch2000: 'Schlachter 2000 (SCH2000)'
};

// Language display names
const LANGUAGE_NAMES = {
  en: 'English',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  de: 'Deutsch'
};

// Default translations for each language
const DEFAULT_TRANSLATIONS: { [key: string]: string } = {
  fr: 'aelf',
  en: 'kjv',
  it: 'cei08',
  es: 'bl95',
  de: 'eu'
};

// Helper function to parse query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

interface BibleContent {
  content: {
    [key: string]: string;
  };
  metadata: {
    source: string;
    url: string;
    language: string;
    book: string;
    chapter: number;
    fetched: string;
  };
  verses?: {
    [key: string]: {
      text: string;
      number: number;
    }
  };
  reference?: string;
  verseCount?: number;
  translation?: {
    id: string;
    name: string;
  };
}

const BibleContentPage = () => {
  const { lang, book, chapter, verses } = useParams<{ lang?: string; book?: string; chapter?: string; verses?: string }>();

  const query = useQuery();
  const translationParam = query.get('t');

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<{
    translation?: string;
    passage?: string;
    technical?: string;
  } | null>(null);

  // For critical errors that should show the ErrorPage
  const [criticalError, setCriticalError] = useState<{
    title: string;
    message: string;
    technical?: string;
  } | null>(null);

  const [selectedTranslation, setSelectedTranslation] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language); // Add state for selected language
  const [loading, setLoading] = useState<boolean>(true);
  const [bibleContent, setBibleContent] = useState<BibleContent | null>(null);
  const [verseStart, setVerseStart] = useState<number | null>(null);
  const [verseEnd, setVerseEnd] = useState<number | null>(null);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [translationMenuOpen, setTranslationMenuOpen] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isEditingReference, setIsEditingReference] = useState(false);
  const [currentBook, setCurrentBook] = useState<string | undefined>(book);
  const [currentChapter, setCurrentChapter] = useState<string | undefined>(chapter);
  const [currentVerses, setCurrentVerses] = useState<string | undefined>(verses);

  // Add state for separate reference form inputs
  const [bookInput, setBookInput] = useState('');
  const [chapterInput, setChapterInput] = useState('');
  const [startVerseInput, setStartVerseInput] = useState('');
  const [endVerseInput, setEndVerseInput] = useState('');

  // Add state for reference validation error
  const [referenceValidationError, setReferenceValidationError] = useState<string | null>(null);

  // Add new state for text scaling
  const [textScale, setTextScale] = useState<number>(1);
  const [initialDistance, setInitialDistance] = useState<number | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [lastScale, setLastScale] = useState<number>(1);

  // Add zoom sensitivity configuration
  const ZOOM_SENSITIVITY = 0.8; // Reduce this value to make zooming more gradual
  const MIN_SCALE = 0.8;
  const MAX_SCALE = 2.0;

  // --- SWIPE STATE ---
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchEndY, setTouchEndY] = useState<number | null>(null);

  // --- SWIPE HANDLERS ---
  const handleSwipeTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setTouchStartX(e.touches[0].clientX);
      setTouchEndX(null);
      setTouchStartY(e.touches[0].clientY);
      setTouchEndY(null);
    }
  };

  const handleSwipeTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && touchStartX !== null && touchStartY !== null) {
      setTouchEndX(e.touches[0].clientX);
      setTouchEndY(e.touches[0].clientY);
    }
  };

  const handleSwipeTouchEnd = () => {
    if (
      touchStartX !== null && touchEndX !== null &&
      touchStartY !== null && touchEndY !== null
    ) {
      const deltaX = touchEndX - touchStartX;
      const deltaY = touchEndY - touchStartY;
      const SWIPE_THRESHOLD = 60; // px, minimum horizontal movement
      const MAX_VERTICAL_DELTA = 40; // px, maximum vertical movement allowed for a horizontal swipe

      if (Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaY) < MAX_VERTICAL_DELTA) {
        if (deltaX > 0) {
          goToPreviousChapter();
        } else if (deltaX < 0) {
          goToNextChapter();
        }
      }
    }
    setTouchStartX(null);
    setTouchEndX(null);
    setTouchStartY(null);
    setTouchEndY(null);
  };

  // --- NAVIGATION HELPERS ---
  const getMaxChapterForBook = (bookAcronym: string): number | null => {
    console.log(`Looking for max chapters for book: "${bookAcronym}"`);
    
    // Normalize the book acronym - proper capitalization (first letter capital)
    let normalizedAcronym = bookAcronym.trim();
    
    // Handle format: 1jn -> 1Jn, 2p -> 2P, etc. (number followed by letter)
    const numberPrefixMatch = normalizedAcronym.match(/^(\d+)([a-z]+)$/i);
    if (numberPrefixMatch) {
      const [_, number, letters] = numberPrefixMatch;
      normalizedAcronym = number + letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase();
      console.log(`Normalized numeric prefix acronym: "${normalizedAcronym}"`);
    } 
    // Handle regular book names: jn -> Jn, lc -> Lc, etc.
    else if (normalizedAcronym.length > 0) {
      normalizedAcronym = normalizedAcronym.charAt(0).toUpperCase() + normalizedAcronym.slice(1).toLowerCase();
      console.log(`Normalized regular acronym: "${normalizedAcronym}"`);
    }
    
    // First try exact match with normalized acronym
    let otBook = bookLimitsData["Ancien Testament"].find(book => book.book === normalizedAcronym);
    let ntBook = bookLimitsData["Nouveau Testament"].find(book => book.book === normalizedAcronym);
    
    // If no exact match, try case-insensitive search
    if (!otBook && !ntBook) {
      console.log(`No exact match found, trying case-insensitive match for "${normalizedAcronym}"`);
      
      otBook = bookLimitsData["Ancien Testament"].find(
        book => book.book.toLowerCase() === normalizedAcronym.toLowerCase()
      );
      
      ntBook = bookLimitsData["Nouveau Testament"].find(
        book => book.book.toLowerCase() === normalizedAcronym.toLowerCase()
      );
    }
    
    // Return results from either testament
    if (otBook) {
      console.log(`Found in Old Testament: ${otBook.book}, max chapters: ${otBook.max_chapters}`);
      return otBook.max_chapters;
    }
    
    if (ntBook) {
      console.log(`Found in New Testament: ${ntBook.book}, max chapters: ${ntBook.max_chapters}`);
      return ntBook.max_chapters;
    }
    
    // Log all books from the JSON to debug
    console.log("Book not found in either testament. Debugging data:");
    console.log(`- Looking for: "${normalizedAcronym}" (original: "${bookAcronym}")`);
    console.log("- Old Testament books:", bookLimitsData["Ancien Testament"].map(b => b.book));
    console.log("- New Testament books:", bookLimitsData["Nouveau Testament"].map(b => b.book));
    
    // Show any potential close matches to help debugging
    console.log("Checking for potential close matches:");
    bookLimitsData["Ancien Testament"].forEach(b => {
      if (b.book.toLowerCase() === normalizedAcronym.toLowerCase()) {
        console.log(`Case-insensitive match found in OT: "${b.book}" vs "${normalizedAcronym}"`);
      }
    });
    bookLimitsData["Nouveau Testament"].forEach(b => {
      if (b.book.toLowerCase() === normalizedAcronym.toLowerCase()) {
        console.log(`Case-insensitive match found in NT: "${b.book}" vs "${normalizedAcronym}"`);
      }
    });
    
    console.log("Book not found, returning null");
    return null; // Book not found
  };

  const getPrevNextChapter = () => {
    if (!currentBook || !currentChapter) {
      console.log("Missing book or chapter, can't determine navigation");
      return { prev: null, next: null };
    }
    
    const chapterNum = parseInt(currentChapter, 10);
    if (isNaN(chapterNum)) {
      console.log("Invalid chapter number, can't determine navigation");
      return { prev: null, next: null };
    }

    console.log(`Calculating navigation for: ${currentBook} ${chapterNum}`);
    
    // Check if this is the first chapter
    const isFirstChapter = chapterNum === 1;
    
    // Get the maximum chapter number for this book with case-insensitive matching
    const maxChapter = getMaxChapterForBook(currentBook);
    console.log(`Max chapter for ${currentBook}: ${maxChapter}, current chapter: ${chapterNum}`);
    
    // Check if this is the last chapter
    const isLastChapter = maxChapter !== null && chapterNum >= maxChapter;
    console.log(`Is last chapter? ${isLastChapter}`);
    
    // Previous chapter only if not the first chapter
    const prev = isFirstChapter ? null : { book: currentBook, chapter: chapterNum - 1 };
    
    // Next chapter only if not the last chapter
    const next = isLastChapter ? null : { book: currentBook, chapter: chapterNum + 1 };

    console.log("Navigation options:", { prev, next });
    return { prev, next };
  };

  const goToPreviousChapter = () => {
    const { prev } = getPrevNextChapter();
    if (prev) {
      // Preserve ?t=... in navigation
      const tParam = selectedTranslation ? `?t=${selectedTranslation}` : '';
      navigate(`/${urlLang}/${prev.book}/${prev.chapter}${tParam}`);
    }
  };

  const goToNextChapter = () => {
    const { next } = getPrevNextChapter();
    if (next) {
      // Preserve ?t=... in navigation
      const tParam = selectedTranslation ? `?t=${selectedTranslation}` : '';
      navigate(`/${urlLang}/${next.book}/${next.chapter}${tParam}`);
    }
  };

  // --- BEGIN: Language detection from URL path ---
  // Extract language code from the URL path (e.g. /fr/ex/1)
  const location = useLocation();
  const pathLangMatch = location.pathname.match(/^\/([a-z]{2})(\/|$)/i);
  const urlLang = pathLangMatch ? pathLangMatch[1] : undefined;
  // --- END: Language detection from URL path ---

  const { entries: commentaryEntries } = useCommentaryEntries(urlLang, currentBook, currentChapter);

  // Get translations for the selected language - Move this function definition to the top
  const getTranslationsForLanguage = (language: string) => {
    console.log("Getting translations for language:", language);
    const allTranslations = getAllAvailableTranslations();
    return allTranslations.filter((translation) => translation.language === language);
  };

  // Get all available translations regardless of language
  const getAllAvailableTranslations = () => {
    return [
      // French translations
      { id: 'aelf', name: TRANSLATION_NAMES.aelf, language: 'fr' },
      { id: 'lsg', name: TRANSLATION_NAMES.lsg, language: 'fr' },
      { id: 'mar', name: TRANSLATION_NAMES.mar, language: 'fr' },
      { id: 'dar', name: TRANSLATION_NAMES.dar, language: 'fr' },
      { id: 'bdp', name: TRANSLATION_NAMES.bdp, language: 'fr' },
      // English translations
      { id: 'kjv', name: TRANSLATION_NAMES.kjv, language: 'en' },
      { id: 'drb', name: TRANSLATION_NAMES.drb, language: 'en' },
      { id: 'esv', name: TRANSLATION_NAMES.esv, language: 'en' },
      { id: 'niv', name: TRANSLATION_NAMES.niv, language: 'en' },
      // Italian translations
      ...italianTranslations.map((translation) => ({
        id: translation.id,
        name: translation.name,
        language: 'it'
      })),
      // Spanish translations
      ...spanishTranslations.map((translation) => ({
        id: translation.id,
        name: translation.name,
        language: 'es'
      })),
      // German translations
      ...germanTranslations.map((translation) => ({
        id: translation.id,
        name: translation.name,
        language: 'de'
      }))
    ];
  };

  // Add a helper to check if a translation exists
  const isValidTranslationId = (id: string) => {
    const allTranslations = getAllAvailableTranslations();
    return allTranslations.some(t => t.id === id);
  };

  // Handle language menu toggle
  const handleLanguageMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    const newMenuState = !languageMenuOpen;
    setLanguageMenuOpen(newMenuState);

    // Close the other menu if open
    if (newMenuState && translationMenuOpen) {
      setTranslationMenuOpen(false);
    }

    // Disable auto-scrolling when opening the menu, enable it when closing
    setShouldAutoScroll(!newMenuState);
  };

  // Handle translation menu toggle
  const handleTranslationMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    const newMenuState = !translationMenuOpen;
    setTranslationMenuOpen(newMenuState);

    // Close the other menu if open
    if (newMenuState && languageMenuOpen) {
      setLanguageMenuOpen(false);
    }

    // Disable auto-scrolling when opening the menu, enable it when closing
    setShouldAutoScroll(!newMenuState);
  };

  // Handle language change
  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    const defaultTranslation = DEFAULT_TRANSLATIONS[language] || '';
    setSelectedTranslation(defaultTranslation); // Automatically select the default translation
    setLanguageMenuOpen(false); // Close only the language dropdown
    setShouldAutoScroll(true); // Re-enable auto-scrolling

    // Save the selected language and translation to localStorage
    localStorage.setItem('selectedLanguage', language);
    localStorage.setItem(`selectedTranslation_${language}`, defaultTranslation);

    // Reset content to trigger re-fetch with new translation
    setBibleContent(null);
    setLoading(true);
  };

  // Handle translation change
  const handleTranslationChange = (translation: string) => {
    console.log("Changing translation to:", translation);
    setSelectedTranslation(translation);
    setTranslationMenuOpen(false); // Close only the translation dropdown
    setShouldAutoScroll(true); // Re-enable auto-scrolling

    // Save preference to localStorage
    localStorage.setItem(`selectedTranslation_${selectedLanguage}`, translation);

    // Reset content to trigger re-fetch
    setBibleContent(null);
    setLoading(true);
  };

  // Update internal state when route params change
  useEffect(() => {
    setCurrentBook(book);
    setCurrentChapter(chapter);
    setCurrentVerses(verses);
  }, [book, chapter, verses]);

  // Effect to initialize dark mode based on system preference
  useEffect(() => {
    // Check if the user prefers dark mode
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDarkMode(prefersDark);

    // Listen for changes in system preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setDarkMode(e.matches);
    };

    // Add listener for system preference changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Effect to apply dark mode class to body
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
      document.documentElement.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Toggle dark mode handler
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Parse verses range if provided
  useEffect(() => {
    if (currentVerses) {
      const range = currentVerses.split('-');
      if (range.length > 0) {
        setVerseStart(parseInt(range[0], 10));
        setVerseEnd(range.length > 1 ? parseInt(range[1], 10) : parseInt(range[0], 10));
      }
    } else {
      // When no verses are provided, set to null to indicate full chapter
      setVerseStart(null);
      setVerseEnd(null);
    }
  }, [currentVerses]);

  // Set language and translation just once on initial load
  useEffect(() => {
    if (!initialLoad) return; // Only run on initial load

    // Set language and translation preference with updated priorities:
    // 1. Language from URL path (if present and supported)
    // 2. localStorage
    // 3. i18n.language
    // 4. Default to 'en'
    const supportedLangs = Object.keys(LANGUAGE_NAMES);
    const langFromUrl = urlLang && supportedLangs.includes(urlLang) ? urlLang : undefined;
    const savedLanguage = langFromUrl
      || localStorage.getItem('selectedLanguage')
      || i18n.language
      || 'en';

    // Translation: 1. URL param, 2. localStorage, 3. default for language
    const savedTranslation =
      translationParam
      || localStorage.getItem(`selectedTranslation_${savedLanguage}`)
      || DEFAULT_TRANSLATIONS[savedLanguage];

    // If language changed, update i18n as well
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }

    setSelectedLanguage(savedLanguage);
    setSelectedTranslation(savedTranslation);
    setInitialLoad(false); // Mark initial load as complete
  // Add urlLang and location as dependencies
  }, [initialLoad, i18n, translationParam, urlLang, location]);

  // Quand aucun passage n'est fourni (route /:lang/bible-content),
  // éviter le spinner infini et afficher directement l'éditeur de référence.
  useEffect(() => {
    if (!book || !chapter) {
      setLoading(false);
      setIsEditingReference(true);
    }
  }, [book, chapter]);

  // Fetch content only when we have the necessary data
  useEffect(() => {
    if (!selectedTranslation || !currentBook || !currentChapter) return;

    // Check for invalid translation id before fetching
    if (!isValidTranslationId(selectedTranslation)) {
      setValidationError(t('errors.invalidTranslationId') || "This translation does not exist.");
      setErrorDetails({
        translation: selectedTranslation.toUpperCase(),
        passage: `${currentBook} ${currentChapter}`,
        technical: `Translation ID "${selectedTranslation}" does not exist.`
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    // Reset errors
    setValidationError(null);
    setErrorDetails(null);
    setCriticalError(null);

    const fetchBibleContent = async () => {
      try {
        // STEP 1: Validate reference before making API call
        const translationLanguage = getLanguageForTranslation(selectedTranslation);
        const tradition = getTraditionForTranslation(selectedTranslation);

        // The book is already normalized when saved to currentBook
        const chapterNum = parseInt(currentChapter, 10);

        // Validate the complete reference
        // We're using UI language (i18n.language) for error messages only, 
        // but the book name validation uses the translation's language
        const validation = validateReference(
          currentBook,  // already normalized
          chapterNum,
          verseStart || undefined,
          verseEnd || undefined,
          selectedTranslation,
          i18n.language  // UI language for error messages
        );

        // If reference is not valid, show the error and stop
        if (!validation.valid) {
          setValidationError(t('errors.invalidReference'));
          setErrorDetails({
            translation: selectedTranslation.toUpperCase(),
            passage: `${currentBook} ${currentChapter}`,
            technical: validation.error
          });
          setLoading(false);
          return;
        }

        // STEP 2: Reference is valid, proceed with API call
        // Get the proper API book code for the request
        const bookAbbreviation = getApiBookCode(currentBook);

        // Use the abbreviation in the API call
        const passageStr = `${bookAbbreviation} ${currentChapter}`;

        // Ensure the Bible ID is always lowercase when sent to the API
        const exactUrl = `https://europe-west1-bible4md.cloudfunctions.net/getBibleContentHttp?bible=${selectedTranslation.toLowerCase()}&passage=${encodeURIComponent(passageStr)}`;

        // Increase timeout duration for fetching content
        const timeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timed out')), 30000) // Increased to 30 seconds
        );

        const fetchPromise = fetch(exactUrl);
        const response = await Promise.race([fetchPromise, timeout]);

        if (response instanceof Response) {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const textData = await response.text();

          try {
            const data = JSON.parse(textData);

            if (data.error) {
              throw new Error(data.error);
            }

            const transformedData = {
              ...data,
              verses: Object.entries(data.content || {}).reduce((acc, [key, value]) => {
                acc[key] = {
                  text: value as string,
                  number: parseInt(key, 10)
                };
                return acc;
              }, {} as {[key: string]: {text: string, number: number}}),
              reference: `${currentBook} ${currentChapter}`,
              verseCount: Object.keys(data.content || {}).length,
              translation: {
                id: selectedTranslation,
                name: TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] || selectedTranslation
              }
            };

            if (transformedData && Object.keys(transformedData.content || {}).length > 0) {
              setBibleContent(transformedData);
            } else {
              setValidationError(t('errors.contentEmpty'));
              setErrorDetails({
                translation: selectedTranslation.toUpperCase(),
                passage: `${currentBook} ${currentChapter}`,
                technical: "No verses found in the response"
              });
            }
          } catch (jsonError) {
            setValidationError(t('errors.jsonParsing'));
            setErrorDetails({
              translation: selectedTranslation.toUpperCase(),
              passage: `${currentBook} ${currentChapter}`,
              technical: jsonError instanceof Error ? jsonError.message : 'Unknown error'
            });
          }
        } else {
          throw new Error("Unexpected response type");
        }
      } catch (error) {
        setValidationError(t('errors.fetchFailed'));
        setErrorDetails({
          translation: selectedTranslation.toUpperCase(),
          passage: `${currentBook} ${currentChapter}`,
          technical: error instanceof Error ? error.message : 'An unknown error occurred'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBibleContent();
  }, [currentBook, currentChapter, selectedTranslation, t, i18n.language, verseStart, verseEnd]);

  // Add an additional useEffect to debug when bibleContent changes
  useEffect(() => {
    console.log("Bible content state updated:", bibleContent);
    console.log("Verses available:", bibleContent?.verses ? Object.keys(bibleContent.verses).length : 0);
    console.log("Loading finished, final state:", { 
      bibleContent, 
      error: validationError
    });
  }, [bibleContent, validationError]);

  // Add touch event handlers
  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setInitialDistance(distance);
      setLastScale(textScale);
      setIsZooming(true);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isZooming && initialDistance && e.touches.length === 2) {
      const currentDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );

      // Calculate the scale difference with sensitivity factor
      const scaleDiff = ((currentDistance / initialDistance) - 1) * ZOOM_SENSITIVITY;
      const newScale = Math.min(Math.max(lastScale + scaleDiff, MIN_SCALE), MAX_SCALE);

      // Apply smooth scaling
      requestAnimationFrame(() => {
        setTextScale(newScale);
      });
    }
  };

  const handleTouchEnd = () => {
    if (isZooming) {
      setInitialDistance(null);
      setIsZooming(false);
      setLastScale(textScale);
    }
  };

  // Add effect to prevent default zoom
  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    // Prevent default zoom behavior
    const preventDefault = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchstart', preventDefault, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchstart', preventDefault);
    };
  }, [textScale, initialDistance, isZooming]);

  // Update the useEffect for zoom behavior to include smoother transitions
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Prevent default browser zoom */
      html {
        touch-action: pan-x pan-y;
        -webkit-text-size-adjust: none;
      }
      
      /* Fixed-size UI elements */
      .ui-fixed-element {
        transform-origin: center;
        transform: scale(1) !important;
        font-size: 16px !important;
      }
      
      /* Scalable bible text with smoother transition */
      .bible-verse-text {
        transform-origin: left top;
        transition: font-size 0.05s cubic-bezier(0.4, 0.0, 0.2, 1);
      }
      
      /* Disable user selection during zoom */
      .zooming {
        user-select: none;
        -webkit-user-select: none;
      }
      
      /* Ensure proper touch handling */
      * {
        touch-action: pan-x pan-y;
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const renderVerses = () => {
    if (!bibleContent) return null;

    // Get verses either from the verses property or transform from content property
    const versesObj = bibleContent.verses ||
      (bibleContent.content ? Object.entries(bibleContent.content).reduce((acc, [key, value]) => {
        acc[key] = { text: value, number: parseInt(key, 10) };
        return acc;
      }, {} as {[key: string]: {text: string, number: number}}) : {});

    if (Object.keys(versesObj).length === 0) return null;

    const versesArray = Object.entries(versesObj)
      .map(([_, verse]) => verse)
      .sort((a, b) => a.number - b.number);

    const commentaryByVerse = new Map<number, CommentaryEntry>();
    commentaryEntries.forEach(entry => {
      if (!commentaryByVerse.has(entry.verseStart)) {
        commentaryByVerse.set(entry.verseStart, entry);
      }
    });

    return (
      <div className="space-y-0">
        {versesArray.map((verse) => {
          // Determine if the verse should be highlighted only for specific verse ranges
          // If verseStart and verseEnd are null (whole chapter view), don't apply highlighting
          const isHighlighted = 
            (verseStart !== null && verseEnd !== null) ? 
              (verse.number >= verseStart && verse.number <= verseEnd) :
              false;

          return (
            <div 
              id={`verse-${verse.number}`}
              key={verse.number}
              className={`${isHighlighted ? 
                (darkMode ? 'bg-amber-900/30 p-2 rounded' : 'bg-amber-100 p-2 rounded') : ''}`}
              ref={(verseStart !== null && isHighlighted && verse.number === verseStart) ? (el) => {
                // Auto-scroll to first highlighted verse only when specific verses are selected
                // AND when shouldAutoScroll is true (menu not open)
                if (el && verseStart && shouldAutoScroll) {
                  setTimeout(() => {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Immediately disable auto-scroll so user can scroll normally after
                    setShouldAutoScroll(false);
                  }, 300);
                }
              } : undefined}
            >
              {commentaryByVerse.has(verse.number) && (() => {
                const entry = commentaryByVerse.get(verse.number)!;
                return (
                  <a
                    href={`/${urlLang}/evangile/${entry.date}`}
                    className="rounded-lg overflow-hidden block"
                    style={{ float: 'left', width: '40%', marginRight: '0.6em', marginBottom: '0.3em' }}
                    aria-label={entry.reference}
                  >
                    <img
                      src={entry.imageThumbUrl}
                      alt={entry.reference}
                      className="w-full h-auto object-cover rounded-lg"
                      loading="lazy"
                    />
                  </a>
                );
              })()}
              <span className={`ui-fixed-element ${darkMode ? 'text-amber-400' : 'text-amber-800'} mr-1 text-xs align-top leading-tight inline-block`}>
                <sup>{verse.number}&nbsp;</sup>
              </span>
              <span
                className={`bible-verse-text ${darkMode ? 'text-gray-200' : ''}`}
                style={{
                  fontSize: `${textScale}em`,
                  transition: 'font-size 0.1s ease-out'
                }}
              >
                {verse.text}
              </span>
            </div>
          );
        })}
        <div style={{ clear: 'both' }} />

        {/* Zoom and navigation controls as a fixed, centered overlay */}
        <div
          className="fixed bottom-4 inset-x-0 flex flex-row items-center justify-between ui-fixed-element z-40 px-4"
          style={{ 
            padding: '8px',
            margin: '0 8px'
          }}
        >
          {/* Previous chapter button - only show if not first chapter */}
          {getPrevNextChapter().prev ? (
            <button
              onClick={goToPreviousChapter}
              className={`p-2 rounded-full shadow flex items-center justify-center w-10 h-10
                ${darkMode 
                  ? 'bg-amber-800 hover:bg-amber-700 text-amber-100' 
                  : 'bg-amber-300 hover:bg-amber-400 text-amber-900'}`}
              aria-label={t('bible_content.previousChapter') || 'Previous Chapter'}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          ) : <div className="w-10"></div>} {/* Empty div to maintain spacing */}
          
          {/* Centered zoom controls */}
          <div className="flex items-center justify-center space-x-2">
            {/* Zoom out (-) */}
            <button
              onClick={() => setTextScale(scale => Math.max(0.8, scale - 0.1))}
              className={`p-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-white'} shadow flex items-center justify-center w-10 h-10 border ${
                darkMode ? 'border-amber-800' : 'border-amber-300'
              }`}
              aria-label="Decrease text size"
            >
              <span className="text-lg font-bold" style={{lineHeight: 1}}>-</span>
            </button>
            {/* Reset zoom */}
            <button
              onClick={() => setTextScale(1)}
              className={`p-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-white'} shadow flex items-center justify-center w-10 h-10 border ${
                darkMode ? 'border-amber-800' : 'border-amber-300'
              }`}
              aria-label="Reset text size"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v16h16" />
              </svg>
            </button>
            {/* Zoom in (+) */}
            <button
              onClick={() => setTextScale(scale => Math.min(2.0, scale + 0.1))}
              className={`p-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-white'} shadow flex items-center justify-center w-10 h-10 border ${
                darkMode ? 'border-amber-800' : 'border-amber-300'
              }`}
              aria-label="Increase text size"
            >
              <span className="text-lg font-bold" style={{lineHeight: 1}}>+</span>
            </button>
          </div>
          
          {/* Next chapter button - only show if there's a next chapter */}
          {getPrevNextChapter().next ? (
            <button
              onClick={goToNextChapter}
              className={`p-2 rounded-full shadow flex items-center justify-center w-10 h-10
                ${darkMode 
                  ? 'bg-amber-800 hover:bg-amber-700 text-amber-100' 
                  : 'bg-amber-300 hover:bg-amber-400 text-amber-900'}`}
              aria-label={t('bible_content.nextChapter') || 'Next Chapter'}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          ) : <div className="w-10"></div>} {/* Empty div to maintain spacing */}
        </div>
      </div>
    );
  };

  // Function to check if a translation is a French translation
  const isFrenchTranslation = (translation: string): boolean => {
    const frenchTranslations = ['aelf', 'lsg', 'mar', 'dar'];
    return frenchTranslations.includes(translation.toLowerCase());
  };

  // Construct a more standardized reference
  const getFormattedReference = () => {
    // Use the translation language for display in the content area
    const translationLanguage = getLanguageForTranslation(selectedTranslation);

    // Format the reference using translation language for display - ensure full book name is shown
    return formatUIReference(
      currentBook || '',
      currentChapter ?? '',
      verseStart || undefined,
      verseEnd || undefined,
      translationLanguage
    );
  };

  // Function to get the display reference specifically for the header
  const getHeaderReference = () => {
    // Always use the UI language for header display
    const uiLanguage = i18n.language;

    // Special handling for Psalms: display as 'hebrew number (aelf number)' for 9-147
    const isPsalm = (currentBook || '').toLowerCase() === 'ps';
    if (isPsalm && currentChapter) {
      return getPsalmDisplayName(currentChapter, uiLanguage); // Use UI language for input form/header
    }

    // Format the reference using UI language
    return formatUIReference(
      currentBook || '',
      currentChapter ?? '',
      verseStart || undefined,
      verseEnd || undefined,
      uiLanguage
    );
  };

  // Effect to set initial reference input values when toggling edit mode
  useEffect(() => {
    if (isEditingReference && currentBook && currentChapter) {
      // Get translation language for proper book name display
      const translationLanguage = getLanguageForTranslation(selectedTranslation);

      // Get display name in the translation language (not UI language)
      const bookDisplayName = getBookDisplayName(currentBook, translationLanguage);

      // Use display name for the UI form
      setBookInput(bookDisplayName);
      setChapterInput(currentChapter);

      if (currentVerses) {
        const parts = currentVerses.split('-');
        setStartVerseInput(parts[0]);
        if (parts.length > 1) {
          setEndVerseInput(parts[1]);
        } else {
          setEndVerseInput('');
        }
      } else {
        setStartVerseInput('');
        setEndVerseInput('');
      }
    }
  }, [isEditingReference, currentBook, currentChapter, currentVerses, i18n.language]);

  // Function to handle reference form submission with separate fields
  const handleReferenceSubmit = (e: FormEvent) => {
    e.preventDefault();

    try {
      // Basic validation using UI language for error messages
      if (!bookInput.trim()) {
        throw new Error(i18n.language === 'fr' ? 
          "Veuillez saisir un nom de livre" : 
          "Please enter a book name");
      }

      if (!chapterInput.trim() || isNaN(parseInt(chapterInput, 10))) {
        throw new Error(i18n.language === 'fr' ?
          "Veuillez saisir un numéro de chapitre valide" :
          "Please enter a valid chapter number");
      }

      // Always use UI language (from i18n) for form input processing
      const uiLanguage = i18n.language;
      console.log(`Processing form with UI language: ${uiLanguage}`);

      // Log the book input before processing
      console.log(`Book input to normalize: "${bookInput.trim()}"`);

      // Use normalizeBookToAcronym with explicit UI language parameter
      const standardizedBookAcronym = normalizeBookToAcronym(bookInput.trim(), uiLanguage);

      console.log(`Book input normalized to acronym: "${standardizedBookAcronym}" using UI language: ${uiLanguage}`);

      // Parse chapter and verse numbers
      const chapterNum = parseInt(chapterInput.trim(), 10);
      const startVerseNum = startVerseInput ? parseInt(startVerseInput, 10) : undefined;
      const endVerseNum = endVerseInput ? parseInt(endVerseInput, 10) : undefined;

      // Validate the reference with the standard acronym
      const validation = validateReference(
        standardizedBookAcronym,
        chapterNum,
        startVerseNum,
        endVerseNum,
        selectedTranslation,
        i18n.language  // UI language for error messages
      );

      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Reset validation error if valid
      setReferenceValidationError(null);

      // 1. Update the internal state variables with the standardized acronym
      setCurrentBook(standardizedBookAcronym);
      setCurrentChapter(chapterInput.trim());

      // 2. Set verse range if specified
      if (startVerseInput) {
        if (endVerseInput) {
          setCurrentVerses(`${startVerseInput}-${endVerseInput}`);
        } else {
          setCurrentVerses(startVerseInput);
        }
      } else {
        setCurrentVerses(undefined);
      }

      // 3. Exit edit mode and re-enable auto-scroll for the new content
      setIsEditingReference(false);
      setShouldAutoScroll(true);

      // 4. Reset content and trigger loading state to fetch new content
      setBibleContent(null);
      setLoading(true);

      // Also reset any previous error state
      setValidationError(null);
      setErrorDetails(null);
      setCriticalError(null);

    } catch (error) {
      // Set reference validation error for display in the form
      if (error instanceof Error) {
        setReferenceValidationError(error.message);
      } else {
        setReferenceValidationError('An unknown error occurred');
      }
    }
  };

  // Function to get API-compatible book abbreviation
  const getApiBookAbbreviation = (book: string, translation: string): string => {
    // Get the UI language to normalize the input appropriately
    const uiLanguage = i18n.language;

    // First normalize the book name using the UI language to handle input correctly
    const bookAcronym = normalizeBookToAcronym(book, uiLanguage);

    // Get the API-compatible code
    const apiCode = getApiBookCode(bookAcronym);

    console.log(`API book code for "${book}": "${apiCode}" (using UI language: ${uiLanguage})`);

    return apiCode;
  };

  // Render error message inside the card
  const renderErrorMessage = () => {
    const translationName = TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] 
      ? `${selectedTranslation.toUpperCase()} (${TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES].split(' ')[0]})`
      : selectedTranslation.toUpperCase();

    // Check if this is a validation error rather than a network/server error
    const isValidationError = errorDetails?.technical && 
      (errorDetails.technical.includes("does not exist") || 
       errorDetails.technical.includes("n'existe pas"));

    // Add a specific message for invalid translation id
    const isInvalidTranslationId = errorDetails?.technical && errorDetails.technical.includes('does not exist');

    return (
      <div className="space-y-4 py-6 text-center">
        <div className={`rounded-full mx-auto flex items-center justify-center w-16 h-16 ${
          darkMode ? 'bg-red-900/30' : 'bg-red-100'
        }`}>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className={`h-8 w-8 ${darkMode ? 'text-red-400' : 'text-red-600'}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>

        <h3 className={`text-xl font-semibold ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
          {isInvalidTranslationId
            ? (t('errors.invalidTranslationId') || "This translation does not exist.")
            : t('errors.loadingFailed')}
        </h3>

        {errorDetails && (
          <div className="space-y-2">
            <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
              {t('errors.couldNotLoad', { 
                translation: translationName,
                passage: `${currentBook ? currentBook.charAt(0).toUpperCase() + currentBook.slice(1) : ''} ${currentChapter || ''}`
              })}
            </p>

            <div className={`text-sm mt-4 p-3 rounded ${
              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
            }`}>
              <p>
                {isInvalidTranslationId
                  ? t('errors.invalidTranslationIdDetails') || "The translation you requested does not exist. Please select another translation."
                  : (isValidationError ? t('errors.invalidBook') : t('errors.tryAnother'))}
              </p>
            </div>

            {errorDetails.technical && (
              <details className="mt-4">
                <summary className={`text-sm cursor-pointer ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {t('errors.technicalDetails')}
                </summary>
                <div className={`text-xs mt-2 p-2 rounded font-mono overflow-auto max-h-32 ${
                  darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                }`}>
                  {errorDetails.technical}
                </div>
              </details>
            )}
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={() => {
              // Try another translation
              if (selectedTranslation === 'aelf') {
                handleTranslationChange('kjv');
              } else if (selectedTranslation === 'kjv') {
                handleTranslationChange('aelf');
              } else {
                handleTranslationChange('aelf');
              }
            }}
            className={`px-4 py-2 rounded-lg ${
              darkMode 
                ? 'bg-amber-700 hover:bg-amber-600 text-white' 
                : 'bg-amber-600 hover:bg-amber-700 text-white'
            }`}
          >
            {t('errors.tryDifferentTranslation')}
          </button>
        </div>
      </div>
    );
  };

  // Move the zoom behavior useEffect here, among the other hooks
  // and BEFORE any conditional returns
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Prevent default browser zoom */
      html {
        touch-action: pan-x pan-y;
        -webkit-text-size-adjust: none;
      }
      
      /* Fixed-size UI elements */
      .ui-fixed-element {
        transform-origin: center;
        transform: scale(1) !important;
        font-size: 16px !important;
      }
      
      /* Scalable bible text */
      .bible-verse-text {
        transform-origin: left top;
        transition: font-size 0.1s ease-out;
      }
      
      /* Disable user selection during zoom */
      .zooming {
        user-select: none;
        -webkit-user-select: none;
      }
      
      /* Ensure proper touch handling */
      * {
        touch-action: pan-x pan-y;
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add a useEffect for fixing initial text size on mobile
  useEffect(() => {
    // Create a style element for mobile-specific styling
    const mobileStyle = document.createElement('style');
    mobileStyle.textContent = `
      @media (max-width: 767px) {
        html {
          font-size: 16px; /* Control base font size */
        }
        
        /* Ensure text wrapping and prevent overflow */
        p, h1, h2, h3, h4, h5, h6, span, div {
          overflow-wrap: break-word;
          word-wrap: break-word;
          -ms-word-break: break-word;
          word-break: break-word;
          max-width: 100%;
        }
        
        /* Make content containers adaptive */
        .container {
          width: 100%;
          max-width: 100%;
          padding-left: 16px;
          padding-right: 16px;
        }
      }
    `;

    document.head.appendChild(mobileStyle);

    // Clean up on unmount
    return () => {
      document.head.removeChild(mobileStyle);
    };
  }, []);

  // Also in the component's initialization, check if translations are loaded
  useEffect(() => {
    // Force reload translations if they're not properly loaded
    if (!t('errors.loadingFailed') || t('errors.loadingFailed') === 'errors.loadingFailed') {
      console.warn('Translations not properly loaded, attempting to reload');
      i18n.reloadResources();
    }
  }, [t, i18n]);

  // Add a basic reference validation helper that supports both English and French formats
  const isValidReferenceFormat = (input: string) => {
    // More permissive regex to match a wider range of inputs
    const regex = /^([a-zA-Z]+\.?\s*[a-zA-Z]*)\s+(\d+)(?:[:\s,](\d+)(?:-(\d+))?)?$/i;
    return regex.test(input.trim());
  };

  // Disable auto-scrolling when entering edit mode
  const toggleReferenceEditMode = () => {
    const newEditingState = !isEditingReference;
    setIsEditingReference(newEditingState);

    // Disable auto-scrolling when entering edit mode
    if (newEditingState) {
      setShouldAutoScroll(false);
    } else {
      // Re-enable auto-scrolling when exiting edit mode without submitting
      setShouldAutoScroll(true);
    }
  };

  // Return the ErrorPage component for critical errors
  // This conditional return must come AFTER all hook calls
  if (criticalError) {
    return (
      <ErrorPage
        title={criticalError.title}
        message={criticalError.message}
        technical={criticalError.technical}
      />
    );
  }

  // Generate dynamic metadata
  const pageTitle = bibleContent
    ? `${getFormattedReference()} — ${TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] || selectedTranslation} — Bible4Me`
    : 'Bible Viewer — Bible4Me';

  const pageDescription = bibleContent
    ? `${t('seo.description', {
        reference: getFormattedReference(),
        translation: TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] || selectedTranslation,
      })}`
    : t('seo.defaultDescription');

  const supportedLanguages = ['en', 'fr', 'it', 'es', 'de']; // Example supported languages
  const baseUrl = 'https://bible4.me'; // Example base URL
  const hreflangs = supportedLanguages.map((lang) => ({
    lang,
    url: `${baseUrl}/${lang}/${currentBook}/${currentChapter}${currentVerses ? `/${currentVerses}` : ''}?t=${DEFAULT_TRANSLATIONS[lang] || selectedTranslation}`,
  }));

  // Get display reference for Psalms in the content area as well
  const isPsalm = (currentBook || '').toLowerCase() === 'ps';
  const contentReference = isPsalm && currentChapter ? getPsalmDisplayName(currentChapter, getLanguageForTranslation(selectedTranslation)) : getFormattedReference();

  // Main component render
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Canonical URL for SEO */}
        <link rel="canonical" href={`${baseUrl}/${selectedLanguage}/${currentBook}/${currentChapter}${currentVerses ? `/${currentVerses}` : ''}?t=${selectedTranslation}`} />
        {hreflangs.map(({ lang, url }) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={url} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/fr/${currentBook}/${currentChapter}${currentVerses ? `/${currentVerses}` : ''}?t=aelf`} />
      </Helmet>
      <div
        className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-amber-50 text-gray-900'} transition-colors duration-300`}
        onTouchStart={handleSwipeTouchStart}
        onTouchMove={handleSwipeTouchMove}
        onTouchEnd={handleSwipeTouchEnd}
      >
        {/* Header - Always visible */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-amber-200'} border-b p-4 transition-colors duration-300`}>
          <div className="max-w-3xl mx-auto">
            {/* First row: Logo, title, and controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <a 
                  href="https://bible4.me" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="min-w-[48px] w-12 h-12 flex items-center justify-center transition-colors flex-shrink-0"
                >
                  <img 
                    src="/assets/b4m-logo.png" 
                    alt="Bible4Me Logo" 
                    className="w-full h-full object-contain rounded-lg"
                  />
                </a>
                {/* Title removed to save space on mobile */}
              </div>

              <div className="flex items-center space-x-2">
                {/* Dark Mode Toggle Button - fixed size */}
                <button
                  onClick={toggleDarkMode} 
                  className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 border border-gray-600' : 'bg-amber-50 border border-amber-200'} flex-shrink-0`}
                  title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                  aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {darkMode ? (
                    <SunIcon className="h-5 w-5 text-amber-300" />
                  ) : (
                    <MoonIcon className="h-5 w-5 text-amber-900" />
                  )}
                </button>
                {/* Language Selector Dropdown */}
                <div className="relative">
                  <button
                    onClick={handleLanguageMenuToggle}
                    className={`flex items-center justify-between px-3 py-2 border rounded-lg ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-amber-300 hover:bg-gray-600' 
                        : 'bg-white border-amber-300 text-amber-900 hover:bg-amber-50'
                    }`}
                    aria-expanded={languageMenuOpen}
                    aria-haspopup="true"
                    style={{ minWidth: "80px" }}
                  >
                    <span className="text-sm font-medium truncate max-w-[100px]">
                      {LANGUAGE_NAMES[selectedLanguage as keyof typeof LANGUAGE_NAMES] || selectedLanguage}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 ml-2 flex-shrink-0" />
                  </button>

                  {languageMenuOpen && (
                    <div 
                      className={`absolute right-0 mt-1 w-48 max-w-[calc(100vw-20px)] rounded-md shadow-lg z-10 ${
                        darkMode 
                          ? 'bg-gray-700 border border-gray-600' 
                          : 'bg-white border border-amber-200'
                      }`}
                      style={{ left: '0', right: 'auto', maxHeight: '250px', overflowY: 'auto' }}
                    >
                      <div className="py-1">
                        {Object.entries(LANGUAGE_NAMES).map(([langCode, langName]) => (
                          <button
                            key={langCode}
                            onClick={() => handleLanguageChange(langCode)}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              langCode === selectedLanguage
                                ? (darkMode 
                                    ? 'bg-amber-700 text-white' 
                                    : 'bg-amber-100 text-amber-900')
                                : (darkMode
                                    ? 'text-gray-200 hover:bg-gray-600' 
                                    : 'text-gray-700 hover:bg-amber-50')
                            }`}
                          >
                            {langName}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Translation Selector Dropdown */}
                <div className="relative">
                  <button
                    onClick={handleTranslationMenuToggle}
                    className={`flex items-center justify-between px-3 py-2 border rounded-lg ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-amber-300 hover:bg-gray-600' 
                        : 'bg-white border-amber-300 text-amber-900 hover:bg-amber-50'
                    }`}
                    aria-expanded={translationMenuOpen}
                    aria-haspopup="true"
                    style={{ minWidth: "80px" }}
                  >
                    <span className="text-sm font-medium truncate max-w-[100px]">
                      {selectedTranslation.toUpperCase() || t('bible_content.selectTranslation')}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 ml-2 flex-shrink-0" />
                  </button>

                  {translationMenuOpen && (
                    <div 
                      className={`absolute right-0 mt-1 w-72 max-w-[calc(100vw-20px)] rounded-md shadow-lg z-10 ${
                        darkMode 
                          ? 'bg-gray-700 border border-gray-600' 
                          : 'bg-white border border-amber-200'
                      }`}
                      style={{ transform: 'translateX(0)', right: '0', left: 'auto' }}
                    >
                      <div className="py-1">
                        {getTranslationsForLanguage(selectedLanguage).map((translation) => (
                          <button
                            key={translation.id}
                            onClick={() => handleTranslationChange(translation.id)}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              translation.id === selectedTranslation
                                ? (darkMode 
                                    ? 'bg-amber-700 text-white' 
                                    : 'bg-amber-100 text-amber-900')
                                : (darkMode
                                    ? 'text-gray-200 hover:bg-gray-600' 
                                    : 'text-gray-700 hover:bg-amber-50')
                            }`}
                          >
                            <span className="font-medium">{translation.id.toUpperCase()}</span>
                            <span className="text-xs ml-2 inline-block">
                              {translation.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Second row: Reference display or input form */}
            <div className={`mt-3 pt-3 border-t border-gray-200 dark:border-gray-700`}>
              {isEditingReference ? (
                <form onSubmit={handleReferenceSubmit} className="flex flex-wrap items-center gap-2">
                  {/* Book input */}
                  <input
                    type="text"
                    value={bookInput}
                    onChange={(e) => {
                      setBookInput(e.target.value);
                      // Clear validation error when typing
                      setReferenceValidationError(null);
                    }}
                    className={`${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-amber-200'} 
                      border rounded px-2 py-1 text-sm ${referenceValidationError ? 
                        'border-red-500 dark:border-red-700' : ''}`}
                    style={{ width: "120px" }}
                    placeholder={t('bible_form.book') ?? ''}
                    autoFocus
                  />

                  {/* Chapter input */}
                  <input
                    type="number"
                    min="1"
                    value={chapterInput}
                    onChange={(e) => {
                      setChapterInput(e.target.value);
                      setReferenceValidationError(null);
                    }}
                    className={`${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-amber-200'} 
                      border rounded px-2 py-1 text-sm ${referenceValidationError ? 
                        'border-red-500 dark:border-red-700' : ''}`}
                    style={{ width: "60px" }}
                    placeholder={t('bible_form.chapter') ?? ''}
                  />

                  <span>:</span>

                  {/* Verse range inputs */}
                  <div className="flex items-center">
                    <input
                      type="number"
                      min="1"
                      value={startVerseInput}
                      onChange={(e) => {
                        setStartVerseInput(e.target.value);
                        setReferenceValidationError(null);
                      }}
                      className={`${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-amber-200'} 
                        border rounded px-2 py-1 text-sm ${referenceValidationError ? 
                          'border-red-500 dark:border-red-700' : ''}`}
                      style={{ width: "50px" }}
                      placeholder={t('bible_form.startVerse') ?? ''}
                    />

                    <span className="mx-1">-</span>

                    <input
                      type="number"
                      min="1"
                      value={endVerseInput}
                      onChange={(e) => {
                        setEndVerseInput(e.target.value);
                        setReferenceValidationError(null);
                      }}
                      className={`${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-amber-200'} 
                        border rounded px-2 py-1 text-sm ${referenceValidationError ? 
                          'border-red-500 dark:border-red-700' : ''}`}
                      style={{ width: "50px" }}
                      placeholder={t('bible_form.endVerse') ?? ''}
                    />
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    className={`px-3 py-1 rounded text-xs flex-shrink-0 ${
                      darkMode 
                        ? 'bg-amber-600 hover:bg-amber-500 disabled:bg-gray-600' 
                        : 'bg-amber-700 hover:bg-amber-600 disabled:bg-gray-300'
                    } text-white`}
                  >
                    {t('bible_content.submitReference')}
                  </button>

                  {/* Cancel button - added */}
                  <button
                    type="button"
                    onClick={toggleReferenceEditMode}
                    className={`px-3 py-1 rounded text-xs flex-shrink-0 ${
                      darkMode 
                        ? 'bg-gray-600 hover:bg-gray-500' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    } text-white ml-2`}
                  >
                    {t('common.cancel')}
                  </button>

                  {/* Display validation error message */}
                  {referenceValidationError && (
                    <div className={`w-full mt-1 text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                      {referenceValidationError}
                    </div>
                  )}
                </form>
              ) : (
                <div className="flex items-center">
                  <div className="flex flex-col">
                    <p className={`${darkMode ? 'text-amber-200' : 'text-amber-700'} text-base`}>
                      {/* Use UI language-formatted reference for header display */}
                      {getHeaderReference()}
                    </p>
                    <p className={`${darkMode ? 'text-amber-400/60' : 'text-amber-700/60'} text-xs`}>
                      {TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] || selectedTranslation}
                    </p>
                  </div>
                  <button 
                    onClick={toggleReferenceEditMode}
                    className={`p-1 rounded-full ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-amber-100'
                    } flex-shrink-0 ml-2`}
                    title={t('bible_content.editReference') || 'Edit Reference'}
                    aria-label={t('bible_content.editReference') || 'Edit Reference'}
                  >
                    <PencilIcon className={`h-3 w-3 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main content container with mobile-friendly padding */}
        <div className="container mx-auto py-4 sm:py-8 px-3 sm:px-4">
          <Card className={`w-full max-w-3xl mx-auto ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-100' : ''}`}>
            <CardContent className="p-4 sm:p-6">
              {loading ? (
                <div className="flex justify-center items-center p-12">
                  <Spinner className={`w-12 h-12 ${darkMode ? 'text-amber-500' : 'text-amber-700'} flex-shrink-0`} />
                  <span className={`ml-3 ${darkMode ? 'text-amber-300' : 'text-amber-900'} text-base`}>{t('bible_content.loading')}</span>
                </div>
              ) : validationError ? (
                renderErrorMessage()
              ) : bibleContent ? (
                <div className="space-y-6">
                  <div className={`border-b pb-4 ${darkMode ? 'border-gray-700' : 'border-amber-200'}`}>
                    <div className="flex flex-wrap items-center justify-between">
                      <div>
                        <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-amber-300' : 'text-amber-900'}`}>
                          {/* For content area, use translation language-formatted reference with full book name */}
                          {contentReference}
                        </h1>
                        <p className={`text-sm ${darkMode ? 'text-amber-400/80' : 'text-amber-700/80'}`}>
                          {TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] || selectedTranslation}
                        </p>
                      </div>
                      {bibleContent?.metadata?.source && bibleContent?.metadata?.url && (
                        <p className={`text-sm ${darkMode ? 'text-amber-400' : 'text-amber-600'} mt-1 sm:mt-0`}>
                          <a 
                            href={bibleContent.metadata.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`underline ${darkMode ? 'hover:text-amber-300' : 'hover:text-amber-800'}`}
                          >
                            {bibleContent.metadata.source}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Bible content rendering */}
                  <div className={`max-w-none ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    {renderVerses()}
                  </div>

                  {/* Commentary footer: horizontal scroll list of chapter entries */}
                  {commentaryEntries.length > 0 && (
                    <div className={`pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-amber-200'}`}>
                      <div
                        className="flex flex-row gap-3 overflow-x-auto pb-2"
                        style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
                      >
                        {commentaryEntries.map((entry, idx) => (
                          <a
                            key={`${entry.date}-${idx}`}
                            href={`/${urlLang}/evangile/${entry.date}`}
                            className={`flex-shrink-0 flex flex-row items-center gap-2 rounded-lg px-2 py-1 border transition-colors no-underline
                              ${darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-amber-50 border-amber-200 hover:bg-amber-100'}`}
                          >
                            {entry.imageThumbUrl && (
                              <img
                                src={entry.imageThumbUrl}
                                alt={entry.reference}
                                className="rounded object-cover flex-shrink-0"
                                style={{ height: '24px', width: 'auto' }}
                                loading="lazy"
                              />
                            )}
                            <span className={`text-xs font-medium whitespace-nowrap ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                              {entry.reference}
                            </span>
                            <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {entry.date}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer section */}
                  <div className={`pt-4 border-t text-sm ${darkMode ? 'border-gray-700 text-amber-400' : 'border-amber-200 text-amber-700'}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                      <p>
                        {t('bible_content.providedBy')}{' '}
                        {bibleContent.metadata?.url ? (
                          <a 
                            href={bibleContent.metadata.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`underline ${darkMode ? 'hover:text-amber-300 text-amber-400' : 'hover:text-amber-800'}`}
                          >
                            {bibleContent.metadata.source || TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] || selectedTranslation}
                          </a>
                        ) : (
                          TRANSLATION_NAMES[selectedTranslation as keyof typeof TRANSLATION_NAMES] || selectedTranslation
                        )}
                      </p>

                      {/* Download App Buttons: App Store & Google Play */}
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <a
                          href="https://apps.apple.com/fr/app/bible4me/id6743235720"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-black text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-900 transition-colors w-full sm:w-auto"
                        >
                          {/* Apple logo */}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" className="inline-block">
                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.38.07 2.34.74 3.16.8 1.2-.24 2.35-.93 3.63-.84 1.54.12 2.7.72 3.46 1.83-3.17 1.9-2.42 6.05.7 7.22-.62 1.5-1.44 2.99-2.95 3.87zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                          </svg>
                          <span>App Store</span>
                        </a>
                        <a
                          href="https://play.google.com/store/apps/details?id=com.bible4md.app"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-black text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-900 transition-colors w-full sm:w-auto"
                        >
                          {/* Google Play logo */}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="inline-block">
                            <path d="M3.18 1.47A1.5 1.5 0 001.5 3v18a1.5 1.5 0 001.68 1.53L14.25 12 3.18 1.47z" fill="#4FC3F7"/>
                            <path d="M17.96 9.3L14.25 12l3.71 2.7 3.35-1.93a1.5 1.5 0 000-2.6L17.96 9.3z" fill="#FFD54F"/>
                            <path d="M14.25 12L3.18 22.53c.17.16.38.28.62.33l12.5-7.16L14.25 12z" fill="#F06292"/>
                            <path d="M14.25 12L3.8 1.14c-.24.05-.45.17-.62.33L14.25 12z" fill="#81C784"/>
                          </svg>
                          <span>Google Play</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className={darkMode ? 'text-gray-200' : 'text-amber-900'}>
                    {t('bible_content.noContent')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default BibleContentPage;
